import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { lockBodyScroll, unlockBodyScroll } from "../utils/scrollLock";

type PromptModalProps = {
  isOpen: boolean;
  title?: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
};

export const PromptModal: React.FC<PromptModalProps> = ({
  isOpen,
  title = "Confirm",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
}) => {
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    lockBodyScroll(); // Lock body scroll when modal opens

    const prevActive = document.activeElement as HTMLElement | null;

    // Focus the confirm button when the modal opens
    const id = window.requestAnimationFrame(() => {
      confirmButtonRef.current?.focus();
      setIsVisible(true);
    });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      unlockBodyScroll(); // Unlock body scroll when modal closes
      document.removeEventListener("keydown", onKeyDown);
      if (prevActive) {
        prevActive.focus();
      }
      window.cancelAnimationFrame(id);
      setIsVisible(false);
      setIsExiting(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const closeWithAnimation = (cb: () => void) => {
    setIsExiting(true);
    setTimeout(() => {
      cb();
    }, 300);
  };

  const handleCancel = () => closeWithAnimation(onCancel);
  const handleConfirm = () => closeWithAnimation(onConfirm);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="prompt-modal-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <div
        onClick={handleCancel}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(2px)",
          opacity: isExiting ? 0 : isVisible ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
      {/* Modal Card */}
      <div
        className="prompt-modal-card"
        style={{
          position: "relative",
          width: "min(560px, 92vw)",
          background: "var(--modal-bg)",
          color: "rgba(255, 255, 255, 0.9)",
          borderRadius: "14px",
          padding: "20px 22px",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          transform: isExiting
            ? "translateY(40px)"
            : isVisible
            ? "translateY(0)"
            : "translateY(40px)",
          opacity: isExiting ? 0 : isVisible ? 1 : 0,
          transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
          boxShadow: "0 25px 50px rgba(0, 0, 0, 0.3)",
        }}
      >
        {title && (
          <div style={{ marginBottom: "8px" }}>
            <h2
              id="prompt-modal-title"
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: 600,
                color: "rgba(255, 255, 255, 0.95)",
              }}
            >
              {title}
            </h2>
          </div>
        )}
        <div style={{ marginBottom: "16px", lineHeight: 1.6 }}>{message}</div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          <button
            type="button"
            onClick={handleCancel}
            style={{
              padding: "10px 14px",
              borderRadius: "10px",
              border: "none",
              background: "rgba(156, 163, 175, 0.5)",
              color: "rgba(255, 255, 255, 0.95)",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(156, 163, 175, 0.7)";
              e.currentTarget.style.color = "rgba(255, 255, 255, 1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(156, 163, 175, 0.5)";
              e.currentTarget.style.color = "rgba(255, 255, 255, 0.95)";
            }}
          >
            {cancelText}
          </button>
          <button
            type="button"
            ref={confirmButtonRef}
            onClick={handleConfirm}
            style={{
              padding: "10px 14px",
              borderRadius: "10px",
              border: "none",
              background: "rgba(16, 185, 129, 0.5)",
              color: "rgba(255, 255, 255, 0.95)",
              cursor: "pointer",
              fontWeight: 600,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(16, 185, 129, 0.7)";
              e.currentTarget.style.color = "rgba(255, 255, 255, 1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(16, 185, 129, 0.5)";
              e.currentTarget.style.color = "rgba(255, 255, 255, 0.95)";
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
